import { registerPlugin } from '@capacitor/core'

// Single registration point. registerPlugin() warns and returns the existing
// proxy if the same name is registered twice, so calling it from each helper
// module produced "Capacitor plugin BlueIDSecretsPlugin already registered"
// on every boot.
export const BlueIDSecretsPlugin = registerPlugin('BlueIDSecretsPlugin')

// Capacitor answers UNIMPLEMENTED when the native class is missing from the
// running binary — which in practice means the JS was updated but the native
// app was not rebuilt. The raw code is opaque in the field, so translate it.
export const describePluginError = err => {
  if (err?.code === 'UNIMPLEMENTED') {
    return 'Access module missing from this build. Rebuild and reinstall the app.'
  }
  return err?.message || 'Something went wrong setting up access.'
}

// BlueID surfaces failures as numeric codes with terse internal names. Map the
// ones a user or tester can actually act on with clear, informative messages.
const UNLOCK_ERRORS = {
  '0': 'Door unlocked successfully.',
  '-1': 'An unexpected error occurred while communicating with the lock.',
  '-3': 'Invalid lock parameters. Please try again.',
  '-4': 'Bluetooth is not ready. Turn Bluetooth off and on again, then retry.',
  '-5': 'This operation is not supported by this lock.',
  '-11': 'Incompatible lock software version. Please contact facility management.',
  '-12': 'Security signature verification failed. Please re-sync your digital key.',
  '-13': 'Your access credential validity period is invalid or has expired.',
  '-14': 'Security encryption error on device. Please restart the app.',
  '-16': 'Lock or resource not found. Please move closer and try again.',
  '-18': 'Connection timed out. Move closer to the door and try again.',
  '-19': 'Bluetooth connection interrupted. Please stay close to the lock and retry.',
  '-21': 'Lock service is currently unavailable. Please try again.',
  '-22': 'Door operation was canceled or interrupted.',
  '-33': 'Incompatible lock hardware detected.',
  '-100': 'No response from door reader.',
  '-101': 'Communication error with door reader.',
  '-103': 'Authentication failed with the door.',
  '-105': 'Door Bluetooth service not found. Move closer and retry.',
  '-106': 'Door Bluetooth characteristic not found. Move closer and retry.',
  '-107': 'Failed to establish Bluetooth session with the lock.',
  '-170': 'Secure communication error with door. Move closer and retry.',
  '-233': 'Phone clock out of sync. Please enable automatic date & time in Settings.',
  '-234': 'Access schedule mismatch. You do not have access to this door at this time.',
  '-237': 'Access schedule mismatch. You do not have access to this door at this time.',
  '-400': 'Digital key credentials not found on this device. Re-synchronizing...',
  '-401': 'Digital key private credentials missing on this device. Re-synchronizing...',
  '-402': 'Door access is not supported on this platform.',
  '-403': 'Door lock not found. Please make sure you are standing near the door and try again.',
  '-404': 'Door access ticket missing or expired. Permissions refreshed, please tap to try again.',
  '-405': 'Access credentials not configured for this door.',
  '-406': 'Access settings not found for this door.',
  '-407': 'Failed to read lock system configuration.',
  '-412': 'Network error while contacting BlueID service.',
  '-416': 'Door connection timed out. Move closer to the door and try again.',
  '-417': 'Failed to authenticate with BlueID Cloud.',
  '-418': 'Failed to update door lock.',
  '-420': 'Failed to retrieve lock status.',
  '-427': 'Your digital key has been revoked. Re-activating key...',
  '-428': 'Your digital key was deleted on the server. Re-registering...',
  '-429': 'Your digital key has expired. Synchronizing permissions...',
}

export const describeUnlockError = err => {
  const code = String(err?.code ?? '')
  if (UNLOCK_ERRORS[code]) return UNLOCK_ERRORS[code]

  const message = String(err?.message || err?.errorMessage || err || '')

  if (/not ?found|no matching device|RemoteDeviceNotFound|SdkDeviceNotFound|-403/i.test(message)) {
    return 'Door lock not found. Please make sure you are standing near the door and try again.'
  }
  if (/no OsSO|OSSid|no token|ticket|-404/i.test(message)) {
    return 'Door access ticket missing or expired. Permissions refreshed, please tap to try again.'
  }
  if (/revoked|-427/i.test(message)) {
    return 'Your digital key was revoked. Re-activating digital key...'
  }
  if (/deleted|-428/i.test(message)) {
    return 'Your digital key was deleted on the server. Re-registering...'
  }
  if (/expired|-429/i.test(message)) {
    return 'Your digital key has expired. Re-synchronizing permissions...'
  }
  if (/clock|timestamp|-233/i.test(message)) {
    return "Phone clock is out of sync. Please ensure your device's date and time are set automatically in Settings."
  }
  if (/schedule|validity|timeperiod|-234|-237/i.test(message)) {
    return 'Access schedule mismatch: your digital key is not valid for this door at this time.'
  }
  if (/bluetooth/i.test(message) && /disabled|off|unavailable/i.test(message)) {
    return 'Bluetooth is turned off. Please enable Bluetooth in Settings to unlock.'
  }
  if (/permission|nearby devices/i.test(message)) {
    return 'Bluetooth or Nearby Devices permission required. Please allow access in Settings.'
  }
  if (/disconnect|interrupted|-19/i.test(message)) {
    return 'Bluetooth connection interrupted. Please stay close to the lock and try again.'
  }
  if (/timeout|-18|-416/i.test(message)) {
    return 'Door connection timed out. Please move closer and try again.'
  }
  if (/busy/i.test(message)) {
    return 'The door is currently busy. Please wait a moment and try again.'
  }
  if (/unauthorized|forbidden|not allowed/i.test(message)) {
    return 'You do not have access permission for this door.'
  }

  return 'Could not open the door. Move closer and try again.'
}
